// http-error.interceptor.ts
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { ToastService } from '../services/toast.service';
import { SubscriptionRestrictionPresentationService } from '../services/subscription-restriction-presentation.service';
import { SessionLogoutCoordinatorService } from '../services/session-logout-coordinator.service';
import { AuthService } from '../services/auth.service';
import { ExpiredSubscriptionAccessService } from '../services/expired-subscription-access.service';
import { catchError, throwError } from 'rxjs';
import { hasShownSessionExpiryToast, markSessionExpiryToastShown } from './session-expiry-toast-state';
import { ERROR_PRESENTATION_OWNER, SUPPRESS_FORBIDDEN_TOAST, SUPPRESS_NOT_FOUND_TOAST } from './http-context-tokens';
import { presentPublicError } from './error-presenter';
import {
  backendErrorDetail,
  backendErrorDetails,
  backendErrorMessageKey,
  backendErrorReason,
  extractBackendError,
  isConcurrentModificationError,
  type BackendErrorResponse
} from './backend-error';

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  if (isAuthLoginRequest(req.url)) {
    return next(req);
  }

  const toast = inject(ToastService);
  const transloco = inject(TranslocoService);
  const router = inject(Router);
  const sessionLogoutCoordinator = inject(SessionLogoutCoordinatorService);
  // Capture ownership for every request, not only refresh. A late ordinary 401
  // from an earlier login must not announce that the replacement session expired.
  const refreshSession = inject(AuthService, { optional: true });
  const accessTokenAtRequest = refreshSession?.accessToken;
  const sessionVersionAtRequest = refreshSession?.sessionVersion;
  const expiredSubscriptionAccess = inject(ExpiredSubscriptionAccessService);
  const restrictionPresentation = inject(SubscriptionRestrictionPresentationService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        const backend: BackendErrorResponse | null = extractBackendError(error);

        if (isExpectedNotFoundRequest(req.url, error.status)) {
          return throwError(() => error);
        }

        // Screens that render their own "not found" state opt out of the generic 404 toast.
        if (error.status === 404 && req.context.get(SUPPRESS_NOT_FOUND_TOAST)) {
          return throwError(() => error);
        }

        if (isSessionExpiryError(req.url, error, backend)) {
          // A refresh started under an older session must not clear or toast a newer login.
          if (refreshSession && (refreshSession.accessToken !== accessTokenAtRequest ||
              (sessionVersionAtRequest !== undefined && refreshSession.isCurrentSessionVersion?.(sessionVersionAtRequest) === false))) {
            return throwError(() => error);
          }
          if (!hasShownSessionExpiryToast()) {
            const sessionExpiryMessageKey = sessionExpiryMessageKeyFor(req.url, backend);
            toast.error(
              translateBackendMessage(
                transloco,
                sessionExpiryMessageKey,
                sessionExpiryFallbackFor(sessionExpiryMessageKey)
              ),
              translateOrFallback(
                transloco,
                'http_error.session_expired_title',
                'Session expired'
              )
            );
            markSessionExpiryToastShown();
          }

          if (isRefreshAccessTokenRequest(req.url)) {
            if (refreshSession?.logout) {
              void refreshSession.logout(false);
            } else {
              // Legacy callers without AuthService cannot safely broadcast an unscoped logout.
              void sessionLogoutCoordinator.logoutLocal({ emitCrossTab: false });
            }
          }

          return throwError(() => error);
        }

        if (isPlanExpiredError(req.url, error, backend)) {
          if (refreshSession && (refreshSession.accessToken !== accessTokenAtRequest ||
              (sessionVersionAtRequest !== undefined && refreshSession.isCurrentSessionVersion?.(sessionVersionAtRequest) === false))) {
            return throwError(() => error);
          }
          const details = backendErrorDetails(backend);
          const access = details?.['effectiveAccessStatus'];
          const status = details?.['subscriptionStatus'];
          // A grace-period write denial is not expiry and must not poison navigation state.
          if (access === 'PAST_DUE') {
            toast.error(
              translateOrFallback(transloco, 'subscription_status.grace',
                'Payment is past due. During the grace period, authorised reads and exports remain available; accounting changes are blocked.'),
              translateOrFallback(transloco, 'billing.status.past_due', 'Past Due')
            );
            return throwError(() => error);
          }
          expiredSubscriptionAccess.update({
            subscriptionActive: false,
            subscriptionExpired: true,
            subscriptionTrial: details?.['trial'] === true,
            effectiveAccessStatus: typeof details?.['effectiveAccessStatus'] === 'string'
              ? details['effectiveAccessStatus'] : undefined,
          });

          // All failures still update policy and reach their callers. Only the burst's first
          // response presents the status/redirect, scoped to the originating session and lifecycle.
          const cancelled = status === 'CANCELLED';
          const paymentRestricted = cancelled || access === 'SUSPENDED' || status === 'SUSPENDED';
          const trialExpired = !paymentRestricted && details?.['trial'] === true;
          const restrictionKind = cancelled ? 'CANCELLED' : paymentRestricted ? 'SUSPENDED'
            : `${access ?? ''}:${status ?? ''}:${details?.['trial'] === true}`;
          const presentRestriction = restrictionPresentation.claim(accessTokenAtRequest, sessionVersionAtRequest, restrictionKind);
          if (presentRestriction && details?.['effectiveAccessStatus'] === 'BILLING_ONLY'
              && !expiredSubscriptionAccess.isAllowedExpiredUrl(router.url)) {
            expiredSubscriptionAccess.redirectToRecovery();
          }

          // A payment restriction retains its lifecycle name; real expiry keeps existing copy.
          if (presentRestriction) toast.error(
            translateOrFallback(transloco,
              paymentRestricted ? 'subscription_status.suspended' : trialExpired
                ? 'http_error.trial_expired_message' : 'http_error.plan_expired_message',
              paymentRestricted
                ? "Access is limited to billing recovery. Your organisation's data is retained."
                : trialExpired ? 'Your free trial has ended. Open Billing to review your options.'
                  : 'Your subscription has expired. Update billing to continue.'),
            translateOrFallback(transloco,
              cancelled ? 'billing.status.cancelled' : paymentRestricted
                ? 'billing.status.suspended' : trialExpired ? 'billing.status.trial_expired' : 'http_error.plan_expired_title',
              cancelled ? 'Cancelled' : paymentRestricted ? 'Suspended' : trialExpired ? 'Trial expired' : 'Subscription expired')
          );

          return throwError(() => error);
        }

        // Optional permission-dependent chrome opts out of the generic "Forbidden" toast
        // (SPEED-865). Deliberately placed after the plan-expired classification above, so an
        // expired subscription still toasts and redirects even on an opted-out request.
        if (error.status === 403 && req.context.get(SUPPRESS_FORBIDDEN_TOAST)) {
          return throwError(() => error);
        }

        const title = titleForError(transloco, error, backend);
        const finalMessage = presentPublicError(error, (key, params) => transloco.translate(key, params)).message;

        // Some screens surface the backend's specific `detail` themselves (e.g. bank-statement import
        // shows which row/date is wrong). Skip the generic fallback toast for those to avoid drowning
        // the actionable message under the generic "Some details are missing or not valid" fallback.
        if (req.context.get(ERROR_PRESENTATION_OWNER) !== 'component'
            && !isComponentHandledErrorRequest(req.url, req.method, error, backend)) {
          // Call toast service
          toast.error(finalMessage, title);
        }
      }

      // rethrow so components can still handle errors if needed
      return throwError(() => error);
    })
  );
};

function isSessionExpiryError(url: string, error: HttpErrorResponse, backend: BackendErrorResponse | null): boolean {
  if (error.status !== 401 && error.status !== 422) {
    return false;
  }

  if (isSessionBoundLogoutRequest(url)) {
    // A rejected logout is not a failed refresh; keep its error visible without clearing a session.
    return false;
  }
  if (isRefreshAccessTokenRequest(url)) {
    return true;
  }

  if (backend && Object.hasOwn(backend, 'errorCode')) {
    return backend.errorCode === 'auth.token_expired' || backend.errorCode === 'auth.token_invalid';
  }

  if (backendErrorReason(backend) === 'TOKEN_EXPIRED' || backendErrorReason(backend) === 'TOKEN_INVALID') {
    return true;
  }

  const backendMessageKey = backendErrorMessageKey(backend);
  if (backendMessageKey === 'http_error.session_expired_message') {
    return true;
  }

  const combined = [
    backend?.title,
    backend?.detail,
    backend?.code,
    backend?.type,
  ]
    .filter((value): value is string => typeof value === 'string')
    .map(value => value.slice(0, 256))
    .join(' ')
    .toLowerCase();

  const hasSessionExpiryHint =
    combined.includes('session expired') ||
    combined.includes('expired due to inactivity') ||
    combined.includes('token expired') ||
    (combined.includes('session') && combined.includes('expire'));

  return hasSessionExpiryHint;
}

function sessionExpiryMessageKeyFor(url: string, backend: BackendErrorResponse | null): string {
  if (isRefreshAccessTokenRequest(url) && isRevokedByNewLogin(backend)) {
    return 'http_error.session_replaced_message';
  }

  return backend?.errorCode === 'auth.token_invalid' || (!backend?.errorCode && backendErrorReason(backend) === 'TOKEN_INVALID')
    ? 'http_error.session_invalid_message'
    : 'http_error.session_expired_message';
}

function sessionExpiryFallbackFor(messageKey: string): string {
  if (messageKey === 'http_error.session_replaced_message') {
    return 'Your account was signed in from another browser or device. Please log in again to continue here.';
  }

  if (messageKey === 'http_error.session_invalid_message') {
    return 'Your session is invalid. Please sign in again.';
  }

  return 'Your session has expired due to inactivity. Please log in again to continue.';
}

/**
 * Standard HTTP reason phrases. Spring's ProblemDetail defaults `title` to the status reason
 * phrase, so an untitled backend error reaches the toast as English text ("Unprocessable Entity")
 * in every language (SPEED-872). A title that matches its status's reason phrase is therefore
 * boilerplate, not a message the backend chose, and is rendered from the locale files instead;
 * Server titles are never presentation input, including nonstandard titles.
 */
const HTTP_REASON_PHRASES: ReadonlyMap<number, string> = new Map([
  [400, 'Bad Request'],
  [401, 'Unauthorized'],
  [403, 'Forbidden'],
  [404, 'Not Found'],
  [409, 'Conflict'],
  [412, 'Precondition Failed'],
  [413, 'Payload Too Large'],
  [422, 'Unprocessable Entity'],
  [428, 'Precondition Required'],
  [429, 'Too Many Requests'],
  [500, 'Internal Server Error'],
  [503, 'Service Unavailable'],
]);

function titleForError(
  transloco: TranslocoService,
  error: HttpErrorResponse,
  backend: BackendErrorResponse | null
): string {
  if (backendErrorReason(backend) === 'ACCOUNT_SETUP_INCOMPLETE') {
    return translateOrFallback(
      transloco,
      'http_error.account_setup_incomplete_title',
      'Account setup incomplete'
    );
  }

  const reasonPhrase = HTTP_REASON_PHRASES.get(error.status);
  if (reasonPhrase) {
    return translateOrFallback(transloco, `http_error.status_${error.status}_title`, reasonPhrase);
  }

  return translateOrFallback(transloco, 'http_error.status_500_title', 'Request failed');
}

function isRevokedByNewLogin(backend: BackendErrorResponse | null): boolean {
  if (backend && Object.hasOwn(backend, 'errorCode')) return false;
  const detail = backendErrorDetail(backend)?.slice(0, 256).toLowerCase() ?? '';
  return detail.includes('token not found') || detail.includes('token expired or unusable');
}

function isPlanExpiredError(
  url: string,
  error: HttpErrorResponse,
  backend: BackendErrorResponse | null
): boolean {
  if (error.status !== 403 || isSignUpRequest(url)) {
    return false;
  }

  return backendErrorReason(backend) === 'PLAN_EXPIRED'
    || backendErrorMessageKey(backend) === 'http_error.plan_expired_message';
}


function translateOrFallback(
  transloco: TranslocoService,
  key: string,
  fallback: string
): string {
  const translated = transloco.translate(key);
  return translated === key ? fallback : translated;
}

function translateBackendMessage(
  transloco: TranslocoService,
  key: string,
  fallback: string
): string {
  return translateOrFallback(transloco, key, fallback);
}

function isAuthLoginRequest(url: string): boolean {
  return /\/auth\/login(?:[/?#]|$)/.test(url);
}

function isSignUpRequest(url: string): boolean {
  return /\/auth\/sign-up(?:[/?#]|$)/.test(url);
}

function isRefreshAccessTokenRequest(url: string): boolean {
  return /\/auth\/refresh-access-token(?:[?#]|$)/.test(url);
}

function isSessionBoundLogoutRequest(url: string): boolean {
  return /\/auth\/refresh-access-token\/logout(?:[?#]|$)/.test(url);
}

function isExpectedNotFoundRequest(url: string, status: number): boolean {
  if (status !== 404) {
    return false;
  }

  return /\/fx\/(rate|latest)(?:[/?#]|$)/.test(url);
}

// Requests whose component already displays the backend's specific `detail` message; the interceptor
// should not add a second, generic toast on top of it.
function isComponentHandledErrorRequest(
  url: string,
  method: string,
  error: HttpErrorResponse,
  backend: BackendErrorResponse | null
): boolean {
  if (method.toUpperCase() === 'POST' && isSignUpRequest(url)) return true;
  // View Bill renders the actionable configuration failure once, keeping the payment dialog open.
  if (method.toUpperCase() === 'POST'
    && /\/bills\/[^/?#]+\/pay(?:[?#]|$)/.test(url)
    && error.status === 409
    && backendErrorMessageKey(backend) === 'http_error.realised_fx_account_unavailable_message') return true;
  if (backendErrorReason(backend) === 'SPEED_NETWORK_CURRENCY_INCOMPATIBLE') return true;
  // The history page redirects on this specific rollout denial. Keep all other 403s visible.
  if (method.toUpperCase() === 'GET'
    && error.status === 403
    && /\/sales\/myinvois\/submission-history(?:[?#]|$)/.test(url)
    && backendErrorReason(backend) === 'FEATURE_DISABLED'
    && backendErrorDetails(backend)?.['featureCode'] === 'MYINVOIS_SUBMISSION_HISTORY') return true;
  if (
    method.toUpperCase() === 'PUT'
    && /\/bills\/[^/?#]+(?:[?#]|$)/.test(url)
    && isConcurrentModificationError(error)
  ) {
    return true;
  }
  return /\/banking\/statements\/(preview|import)(?:[/?#]|$)/.test(url)
    // The invoice CSV import review page renders the backend's row-level reason itself (SPEED-837).
    || /\/invoices\/import\/(preview|commit)(?:[/?#]|$)/.test(url)
    || /\/banking\/ai\/categorize\/bulk(?:\?|$)/.test(url)
    || backendErrorMessageKey(backend) === 'chart_of_accounts.possible_duplicate_conflict';
}
